import { useState, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Box, Container, Grid, Flex, Text, Heading, Button,
  HStack, SimpleGrid
} from '@chakra-ui/react'
import { FiUpload, FiArrowRight, FiLayers } from 'react-icons/fi'
import { stashPendingUploadFile } from '../lib/pendingUpload'

function VStack({ children, gap }: { children: React.ReactNode; gap?: number }) {
  return <Box display="flex" flexDirection="column" gap={gap ? `${gap * 4}px` : undefined}>{children}</Box>
}

const MATERIALS = [
  { name: 'PLA', color: '#4CAF50', tag: 'PLA', slug: 'pla', desc: 'Kolay baskı, geniş renk seçeneği. Prototip ve dekorasyon için ideal.' },
  { name: 'PETG', color: '#2196F3', tag: 'PETG', slug: 'petg', desc: 'Dayanıklı ve su geçirmez. Fonksiyonel parçalar için mükemmel.' },
  { name: 'ABS', color: '#CCF301', tag: 'ABS', slug: 'abs', desc: 'Yüksek ısıya dayanıklı. Mekanik parçalar için tercih edilir.' },
  { name: 'TPU', color: '#FF9800', tag: 'TPU', slug: 'tpu', desc: 'Esnek malzeme. Conta, kılıf ve yumuşak parçalar için.' },
  { name: 'ASA', color: '#9C27B0', tag: 'ASA', slug: 'asa', desc: 'UV dirençli. Dış mekan uygulamaları için optimize edilmiş.' },
  { name: 'Reçine', color: '#F44336', tag: 'Reçine', slug: 'recine', desc: 'Ultra yüksek detay. Mücevher ve minyatür baskılar için.' },
]

const STEPS = [
  { num: '01', title: 'Dosyayı Yükle', desc: 'STL veya OBJ formatında modelinizi sürükle-bırak ile yükleyin.' },
  { num: '02', title: 'Malzeme Seç', desc: 'Malzeme, renk, kalite ve doluluk oranını özelleştirin.' },
  { num: '03', title: 'Fiyat Al', desc: 'Anında detaylı fiyat dökümü alın, sepete ekleyin.' },
  { num: '04', title: 'Teslim Al', desc: 'Ödemeyi tamamlayın, ürününüzü kapınıza kadar alın.' },
]

const FAQS = [
  { q: 'Hangi dosya formatlarını destekliyorsunuz?', a: 'STL ve OBJ formatlarını destekliyoruz. Diğer formatlar için destek ekibimizle iletişime geçebilirsiniz.' },
  { q: 'Baskı süresi ne kadar?', a: 'Siparişiniz 24 saat içinde üretime alınır. Boyuta ve kaliteye göre 1-5 iş günü içinde teslim edilir.' },
  { q: 'Minimum sipariş var mı?', a: 'Minimum sipariş tutarı 50₺\'dir. Adet bazlı indirimler uygulayıyoruz.' },
  { q: 'Renk seçenekleri neler?', a: 'Her malzeme için onlarca renk seçeneğimiz mevcuttur. Stok durumunu anlık olarak görüntüleyebilirsiniz.' },
  { q: 'Modelimi nasıl hazırlamalıyım?', a: 'Modelinizin watertight (su geçirmez) olması ve boyutların mm cinsinden doğru olması yeterlidir.' },
]

export default function Home() {
  const navigate = useNavigate()
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const goToUploadWithFile = useCallback(
    (f: File) => {
      if (f.name.match(/\.(stl|obj|glb|gltf)$/i)) {
        stashPendingUploadFile(f)
        navigate('/tr/yukle')
      }
    },
    [navigate]
  )

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const f = e.dataTransfer.files[0]
    if (f) goToUploadWithFile(f)
  }, [goToUploadWithFile])

  return (
    <Box bg="black" minH="100vh" position="relative" overflow="hidden">
      {/* Noise overlay */}
      <Box className="noise-overlay" />

      {/* ========== HERO ========== */}
      <Box position="relative" minH="100vh" display="flex" alignItems="center" overflow="hidden">
        {/* Glow blobs */}
        <Box className="glow-ellipse" style={{ left: '-75px', top: 0 }} />
        <Box className="glow-ellipse" style={{ right: '-75px', bottom: '200px', left: 'auto', top: 'auto' }} />

        <Container maxW="1200px" pt={24} pb={16}>
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={12} alignItems="center">
            {/* Left */}
            <VStack gap={8}>
              <Box>
                <Text
                  fontSize="sm"
                  color="#CCF301"
                  fontWeight="500"
                  letterSpacing="0.1em"
                  textTransform="uppercase"
                  mb={4}
                >
                  Türkiye'nin 3D Baskı Platformu
                </Text>
                <Heading
                  as="h1"
                  fontSize={{ base: '52px', md: '72px' }}
                  lineHeight="1.1"
                  color="white"
                  style={{ fontFamily: 'Staatliches, sans-serif', fontWeight: 400, textTransform: 'capitalize' }}
                  mb={4}
                >
                  Fikrinden Ürüne —{' '}
                  <Text as="span" color="#CCF301">3D Baskı</Text>{' '}
                  Hizmeti
                </Heading>
                <Text color="#B6B6B3" fontSize="lg" lineHeight="1.8" maxW="500px">
                  STL dosyanızı yükleyin, malzeme ve kalite seçin, anında fiyat alın. Hızlı, hassas ve güvenilir.
                </Text>
              </Box>

              <HStack gap={4} flexWrap="wrap">
                <Button
                  asChild
                  h={12}
                  px={8}
                  bg="#CCF301"
                  color="black"
                  fontWeight="500"
                  borderRadius="8px"
                  _hover={{ opacity: 0.9 }}
                  fontSize="16px"
                >
                  <Link to="/tr/yukle">Dosyayı Yükle</Link>
                </Button>
                <Button
                  asChild
                  h={12}
                  px={8}
                  bg="transparent"
                  color="#CCF301"
                  border="1px solid #CCF301"
                  fontWeight="500"
                  borderRadius="8px"
                  _hover={{ bg: 'rgba(204,243,0,0.08)' }}
                  fontSize="16px"
                >
                  <Link to="/tr/pazar">Model Pazarı</Link>
                </Button>
              </HStack>

              {/* Stats */}
              <HStack gap={8} flexWrap="wrap">
                {[
                  { val: '1.000+', label: 'Tamamlanan Sipariş' },
                  { val: '6+', label: 'Malzeme Çeşidi' },
                  { val: '24s', label: 'Üretime Başlama' },
                ].map((s) => (
                  <Box key={s.label}>
                    <Text fontSize="2xl" fontWeight="700" color="white" style={{ fontFamily: 'Staatliches, sans-serif' }}>{s.val}</Text>
                    <Text fontSize="xs" color="#B6B6B3">{s.label}</Text>
                  </Box>
                ))}
              </HStack>
            </VStack>

            {/* Right: Drop zone — continues on /tr/yukle with the same file */}
            <Box>
              <Box
                border="2px dashed"
                borderColor={dragging ? '#CCF301' : 'rgba(255,255,255,0.1)'}
                borderRadius="16px"
                p={8}
                textAlign="center"
                onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                cursor="pointer"
                bg={dragging ? 'rgba(204,243,0,0.04)' : '#0F0F0F'}
                transition="all 0.2s"
                onClick={() => fileInputRef.current?.click()}
                mb={4}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".stl,.obj,.glb,.gltf"
                  style={{ display: 'none' }}
                  onChange={(e) => e.target.files?.[0] && goToUploadWithFile(e.target.files[0])}
                />
                <Box>
                  <Box color="#CCF301" display="flex" justifyContent="center" mb={3}>
                    <FiUpload size={40} />
                  </Box>
                  <Text color="white" fontWeight="500" mb={1}>STL, OBJ veya GLB sürükleyin</Text>
                  <Text color="#B6B6B3" fontSize="sm">veya tıklayarak seçin — model yükleme sayfasında devam edersiniz</Text>
                </Box>
              </Box>

              <Box bg="#0F0F0F" border="1px solid rgba(255,255,255,0.06)" borderRadius="16px" p={5} mb={4}>
                <Text color="#B6B6B3" fontSize="sm" lineHeight="1.7">
                  Dosya seçildiğinde otomatik olarak <Text as="span" color="#CCF301" fontWeight="600">Model Yükle &amp; Fiyat Al</Text> sayfasına yönlendirilirsiniz; önizleme, malzeme ve fiyat orada hesaplanır.
                </Text>
              </Box>

              <Button asChild h={12} w="full" bg="#CCF301" color="black" fontWeight="500" borderRadius="8px" _hover={{ opacity: 0.9 }}>
                <Link to="/tr/yukle">Doğrudan yükleme sayfasına git</Link>
              </Button>
            </Box>
          </Grid>
        </Container>
      </Box>

      {/* ========== HOW IT WORKS ========== */}
      <Box bg="rgba(0,0,0,0.4)" py={20} position="relative">
        <Container maxW="1200px">
          <Box mb={12}>
            <Text color="#CCF301" fontSize="sm" fontWeight="500" textTransform="uppercase" letterSpacing="0.1em" mb={2}>
              Nasıl çalışır?
            </Text>
            <Heading
              color="white"
              fontSize={{ base: '3xl', md: '4xl' }}
              style={{ fontFamily: 'Staatliches, sans-serif', fontWeight: 400, textTransform: 'capitalize' }}
            >
              4 Adımda Ürününüzü Alın
            </Heading>
          </Box>

          <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }} gap={6}>
            {STEPS.map((s, i) => (
              <Box
                key={s.num}
                bg="#0F0F0F"
                border="1px solid rgba(255,255,255,0.1)"
                borderRadius="16px"
                p={6}
                position="relative"
                _hover={{ borderColor: 'rgba(204,243,0,0.3)' }}
                transition="border-color 0.2s"
              >
                <Text
                  fontSize="3xl"
                  fontWeight="600"
                  color="#CCF301"
                  style={{ fontFamily: 'Inter, sans-serif', lineHeight: '26px' }}
                  mb={3}
                >
                  {s.num}
                </Text>
                <Text color="white" fontWeight="500" fontSize="18px" mb={2}>{s.title}</Text>
                <Text color="#B6B6B3" fontSize="14px" lineHeight="24px">{s.desc}</Text>
              </Box>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ========== MATERIALS ========== */}
      <Box bg="rgba(0,0,0,0.4)" py={20} position="relative">
        <Box className="glow-ellipse" style={{ right: '-75px', top: '100px', left: 'auto' }} />

        <Container maxW="1200px">
          <Box textAlign="center" mb={12}>
            <Text color="#CCF301" fontSize="sm" fontWeight="500" textTransform="uppercase" letterSpacing="0.1em" mb={2}>
              Malzemeler
            </Text>
            <Heading
              color="white"
              fontSize={{ base: '3xl', md: '4xl' }}
              style={{ fontFamily: 'Staatliches, sans-serif', fontWeight: 400, textTransform: 'capitalize' }}
              mb={3}
            >
              6+ Filament Çeşidi
            </Heading>
            <Text color="#B6B6B3" maxW="500px" mx="auto">
              Projenize en uygun malzemeyi seçin. Her biri farklı özelliklere sahip.
            </Text>
          </Box>

          <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} gap={4}>
            {MATERIALS.map((m) => (
              <Link key={m.name} to={`/tr/malzeme/${m.slug}`} style={{ textDecoration: 'none' }}>
                <Box
                  bg="#0F0F0F"
                  border="1px solid rgba(255,255,255,0.1)"
                  borderRadius="16px"
                  p={5}
                  textAlign="center"
                  _hover={{ borderColor: '#CCF301', transform: 'translateY(-2px)' }}
                  transition="all 0.2s"
                  cursor="pointer"
                  h="full"
                >
                  <Box
                    w={10}
                    h={10}
                    borderRadius="full"
                  bg={m.color}
                  mx="auto"
                  mb={3}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <FiLayers size={16} color="black" />
                </Box>
                <Text color="white" fontWeight="500" fontSize="lg" mb={1} style={{ fontFamily: 'Staatliches, sans-serif' }}>{m.name}</Text>
                <Text color="#B6B6B3" fontSize="11px" lineHeight="16px">{m.desc.split('.')[0]}.</Text>
              </Box>
            </Link>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ========== BLOG & GUIDES ========== */}
      <Box py={20}>
        <Container maxW="1200px">
          <Flex
            direction={{ base: 'column', md: 'row' }}
            align={{ base: 'flex-start', md: 'center' }}
            justify="space-between"
            mb={10}
            gap={4}
          >
            <Box>
              <Text color="#CCF301" fontSize="sm" fontWeight="500" textTransform="uppercase" letterSpacing="0.1em" mb={2}>
                Bilgi Merkezi
              </Text>
              <Heading
                color="white"
                fontSize={{ base: '3xl', md: '4xl' }}
                style={{ fontFamily: 'Staatliches, sans-serif', fontWeight: 400, textTransform: 'capitalize' }}
              >
                Rehberler ve İpuçları
              </Heading>
            </Box>
            <Button
              asChild
              h="44px"
              px={6}
              bg="transparent"
              color="#CCF301"
              border="1px solid rgba(204,243,1,0.3)"
              fontWeight="500"
              borderRadius="8px"
              _hover={{ bg: 'rgba(204,243,0,0.06)' }}
              fontSize="14px"
              flexShrink={0}
            >
              <Link to="/tr/blog">Tüm Rehberler</Link>
            </Button>
          </Flex>

          <SimpleGrid columns={{ base: 1, md: 2 }} gap={5}>
            <Link to="/tr/rehber/3d-yazici-secimi" style={{ textDecoration: 'none' }}>
              <Box
                bg="#0F0F0F"
                border="1px solid rgba(255,255,255,0.08)"
                borderRadius="14px"
                p={6}
                _hover={{ borderColor: 'rgba(204,243,0,0.3)', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
                cursor="pointer"
                h="full"
              >
                <Text color="#CCF301" fontSize="11px" textTransform="uppercase" letterSpacing="0.1em" mb={3} fontWeight="500">Satın Alma Rehberi</Text>
                <Heading
                  color="white"
                  fontSize={{ base: '22px', md: '26px' }}
                  fontWeight={400}
                  style={{ fontFamily: 'Staatliches, sans-serif' }}
                  mb={3}
                  lineHeight="1.2"
                >
                  3D Yazıcı Seçerken Neye Dikkat Edilmeli?
                </Heading>
                <Text color="#555" fontSize="13px" lineHeight="1.8">FDM vs SLA, build volume, enclosure ve firmware. Yeni başlayanlar için eksiksiz rehber.</Text>
                <Text color="#CCF301" fontSize="12px" mt={4} fontWeight="500">Rehberi Oku →</Text>
              </Box>
            </Link>

            <Link to="/tr/rehber/filament-secimi" style={{ textDecoration: 'none' }}>
              <Box
                bg="#0F0F0F"
                border="1px solid rgba(255,255,255,0.08)"
                borderRadius="14px"
                p={6}
                _hover={{ borderColor: 'rgba(33,150,243,0.4)', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
                cursor="pointer"
                h="full"
              >
                <Text color="#2196F3" fontSize="11px" textTransform="uppercase" letterSpacing="0.1em" mb={3} fontWeight="500">Malzeme Rehberi</Text>
                <Heading
                  color="white"
                  fontSize={{ base: '22px', md: '26px' }}
                  fontWeight={400}
                  style={{ fontFamily: 'Staatliches, sans-serif' }}
                  mb={3}
                  lineHeight="1.2"
                >
                  Hangi Filamentleri Seçmeliyim?
                </Heading>
                <Text color="#555" fontSize="13px" lineHeight="1.8">PLA, PETG, ABS, TPU, ASA ve Reçine karşılaştırması. Kullanım amacına göre doğru seçim.</Text>
                <Text color="#2196F3" fontSize="12px" mt={4} fontWeight="500">Karşılaştırmayı Gör →</Text>
              </Box>
            </Link>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ========== FAQ ========== */}
      <Box py={20}>
        <Container maxW="800px">
          <Box textAlign="center" mb={12}>
            <Text color="#CCF301" fontSize="sm" fontWeight="500" textTransform="uppercase" letterSpacing="0.1em" mb={2}>
              SSS
            </Text>
            <Heading
              color="white"
              fontSize={{ base: '3xl', md: '4xl' }}
              style={{ fontFamily: 'Staatliches, sans-serif', fontWeight: 400, textTransform: 'capitalize' }}
            >
              Sık Sorulan Sorular
            </Heading>
          </Box>

          <VStack gap={3}>
            {FAQS.map((faq, i) => (
              <Box
                key={i}
                bg="#0F0F0F"
                border="1px solid rgba(255,255,255,0.1)"
                borderRadius="12px"
                overflow="hidden"
                _hover={{ borderColor: 'rgba(204,243,0,0.2)' }}
                transition="border-color 0.2s"
              >
                <Flex
                  p={5}
                  justify="space-between"
                  align="center"
                  cursor="pointer"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <Text color="white" fontWeight="500">{faq.q}</Text>
                  <Box
                    color="#CCF301"
                    style={{ transform: openFaq === i ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', flexShrink: 0, marginLeft: 16 }}
                  >
                    <FiArrowRight size={16} style={{ transform: 'rotate(90deg)' }} />
                  </Box>
                </Flex>
                {openFaq === i && (
                  <Box px={5} pb={5}>
                    <Text color="#B6B6B3" fontSize="sm" lineHeight="1.8">{faq.a}</Text>
                  </Box>
                )}
              </Box>
            ))}
          </VStack>
        </Container>
      </Box>

      {/* ========== CTA ========== */}
      <Box py={20} position="relative">
        <Box className="glow-ellipse" style={{ left: '-75px', top: '50%', transform: 'translateY(-50%)' }} />
        <Box className="glow-ellipse" style={{ right: '-75px', top: '50%', transform: 'translateY(-50%)', left: 'auto' }} />

        <Container maxW="1200px">
          <Box
            bg="#0F0F0F"
            border="1px solid rgba(255,255,255,0.15)"
            borderRadius="24px"
            p={{ base: 8, md: 16 }}
            textAlign="center"
            position="relative"
            overflow="hidden"
          >
            <Heading
              color="white"
              fontSize={{ base: '3xl', md: '4xl' }}
              mb={3}
              style={{ fontFamily: 'Staatliches, sans-serif', fontWeight: 400, textTransform: 'capitalize' }}
            >
              Fikrinizi Hayata Geçirmeye Hazır mısınız?
            </Heading>
            <Text color="#B6B6B3" mb={8} fontSize="lg">
              Ücretsiz danışma ile başlayın — Tasarımınız, sınırsız ilham kaynağı.
            </Text>
            <Button
              asChild
              h={14}
              px={10}
              bg="#CCF301"
              color="black"
              fontWeight="500"
              borderRadius="8px"
              _hover={{ opacity: 0.9 }}
              fontSize="16px"
            >
              <Link to="/tr/yukle">Dosyayı Yükle ve Fiyat Al</Link>
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  )
}

